import Transition from '@ember/routing/-private/transition';
import EmberObject from '@ember/object';
import { getOwner } from '@ember/application';
import Route from 'cio/routes/base';
import { PlanFeatures } from 'cio/models/account';
import { Permission } from '@customerio/hydra-permissions';
import { SETTING_LINKS_ALL } from 'cio/pods/settings/index/controller';
import { ProductApp } from './product-app-info';

// in practice always a Route class, but we can't use it directly as these utils
// are used within Route class methods themselves (Route.beforeModel) - breaking
// implicit return types
export type RedirectableContext = EmberObject & { routeName: Route['routeName']; transitionTo: Route['transitionTo'] };

function getParentRouteFromTransition(transition: Transition) {
  let { to } = transition;

  if (to.name.endsWith('.index') && to.parent != null) {
    to = to.parent;
  }

  return to?.parent?.name;
}

function getServices(context: RedirectableContext) {
  let owner = getOwner(context);
  return {
    alerts: owner.lookup('service:alerts'),
    auth: owner.lookup('service:auth'),
    masq: owner.lookup('service:masq'),
    router: owner.lookup('service:router'),
    account: owner.lookup('service:account'),
    features: owner.lookup('service:features'),
  };
}

export function redirectAwayAndAlert(
  context: RedirectableContext,
  transition: Transition,
  opts: {
    // this isn't a perfect type, but enough for our needs
    // (see https://github.com/DefinitelyTyped/DefinitelyTyped/blob/a5183295b54f06c5163cfd4b264bdf93e7a8abde/types/ember__routing/router-service.d.ts#L200 for all possible param combinations)
    redirectTo?: string | [string, { queryParams: object }];
    alertMessage?: string;
  } = {},
) {
  let services = getServices(context);

  let redirectTo: string | [string, { queryParams: object }] =
    opts.redirectTo ?? getParentRouteFromTransition(transition) ?? 'application';

  // when transitionTo("some.route") is called, transition.to.name can be set to "some.route.index"
  let redirectToRouteName = typeof redirectTo === 'string' ? redirectTo : redirectTo[0];
  let transitioningToSameRoute =
    transition != null &&
    (transition.to.name === redirectToRouteName || transition.to.name === `${redirectToRouteName}.index`);

  if (transitioningToSameRoute) {
    return;
  }

  let alertMessage = opts.alertMessage ?? "You're not allowed to view this page. Contact an Admin to update your role.";
  if (alertMessage !== '') {
    services.alerts.error('route.accessDenied', alertMessage, 'Oh no!', 10000);
  }

  // redirectTo could be a string (the route name) or an
  // array, in which case we pass the entire array as arguments (it could
  // be the routeName, model, and queryParams, or any combination of them)
  if (typeof redirectTo === 'string') {
    return services.router.transitionTo(redirectTo);
  }
  return services.router.transitionTo(...redirectTo);
}

export function redirectIfNoPermission(context: RedirectableContext, permission: Permission, transition: Transition) {
  let services = getServices(context);
  if (!services.auth.hasPermission(permission)) {
    return redirectAwayAndAlert(context, transition);
  }

  return;
}

export function redirectToSettingsIfNoPermission(
  context: RedirectableContext,
  permission: Permission,
  transition: Transition,
) {
  let services = getServices(context);
  if (!services.auth.hasPermission(permission)) {
    return redirectAwayAndAlert(context, transition, {
      redirectTo: 'settings',
    });
  }

  return;
}

export function redirectIfViewer(context: RedirectableContext, transition: Transition) {
  let services = getServices(context);

  if (services.auth.user?.isReadOnly ?? true) {
    return redirectAwayAndAlert(context, transition, {
      alertMessage:
        "Sorry! As a viewer, you don't have permission to view that page. If you'd like access, let an Admin know.",
    });
  }

  return;
}

export function redirectIfMasqingReadOnly(context: RedirectableContext, transition: Transition) {
  let services = getServices(context);
  if (services.masq.isOn && services.masq.readOnly) {
    return redirectAwayAndAlert(context, transition, {
      alertMessage: "Sorry! You're in read-only masquerading mode, so you can't view that page.",
    });
  }

  return;
}

export function redirectIfAtWorkspaceLimit(context: RedirectableContext, transition: Transition) {
  let services = getServices(context);
  if (services.account.planLimitReached(PlanFeatures.Workspaces)) {
    return redirectAwayAndAlert(context, transition, {
      alertMessage: 'Sorry! You have reached your plan limit for workspaces. Contact us to upgrade your plan.',
    });
  }
  return;
}

export function redirectIfNoAccessToWarehouseSync(context: RedirectableContext, transition: Transition) {
  let services = getServices(context);
  if (!services.account.planFeatures.warehouseSync.enabled) {
    return redirectAwayAndAlert(context, transition, {
      redirectTo: 'integrations',
      alertMessage: "Sorry! You don't have access to Data Warehouse Sync. Contact us to upgrade your plan.",
    });
  }

  return;
}

export function redirectIfNoJourneysAccess(context: RedirectableContext, transition: Transition, key: string) {
  let services = getServices(context);

  if (
    services.account.isPipelinesOnlyPlan &&
    !services.account.accessibleProducts.includes(ProductApp.JOURNEYS) &&
    SETTING_LINKS_ALL[key].restrictedToProducts?.includes(ProductApp.JOURNEYS) === true
  ) {
    return redirectAwayAndAlert(context, transition, {
      alertMessage: 'This page is only available for Journeys customers.',
    });
  }
  return;
}
